import React, { MouseEvent } from 'react';
import styled from 'styled-components';

import Divider from '@/components/atom/Divider';
import MaterialIcon from '@/components/atom/MaterialIcon';
import { DefaultButtonStyled } from '@/components/atom/buttons/DefaultButton';
import DefaultDoctorProfile from '@/components/atom/icon/DefaultDoctorProfile';
import Dot from '@/components/atom/icon/Dot';
import DotMenu from '@/components/atom/icon/DotMenu';
import { LineClampTextStyled } from '@/components/atom/text/DefaultText';
import ClickLogger from '@/components/common/ClickLogger';
import LinkTag from '@/components/common/link-tag';
import LazyImage from '@/components/molecules/LazyImage';
import NurseProfile from '@/components/molecules/nurse-profile';
import BlockQuestion from '@/components/molecules/question-list/question-item/block-question';

import { FAQ_LOG_EVENT } from '@/constants/logs/faq';
import { QuestionDetailModalDataType } from '@/hooks/qna/useQuestionDetailViewModel';
import { REPORT_TYPE, ROLE_TYPE } from '@/hooks/useReportModal';
import ContentMarkDown from '@/shared/ui/content-mark-down/ContentMarkDown';
import theme from '@/styles/theme';
import { AnswerLikeType } from '@/types/bff/gql-schema';
import { Nullable } from '@/types/common';
import { AnswererType, QnaDetailAnswerVo } from '@/types/qna';
import { absoluteTime, getAnswerDetail, getQndAnswererMedicalStaffType } from '@/utils/format';
import AnswerFootnote from './AnswerFootnote';
import AnswerReview from './AnswerReview';
import AnswerWriteReview from './AnswerWriteReview';
import AnswererHospital from './AnswererHospital';

export interface AnswerDetailProps {
  answer: QnaDetailAnswerVo;
  categoryName: string;
  onShowModal: (e: MouseEvent<HTMLButtonElement>, data: QuestionDetailModalDataType) => void;
  onClickWriteReview: (answerPid: string) => void;
  onClickLikeAnswer: (answerPid: string, type: AnswerLikeType, myLikeType: Nullable<AnswerLikeType>) => void;
  onClickMagazine: (magazinePid: string) => void;
  moveToHospitalDetail: (hospitalPid: string) => void;
  moveToDoctorDetailPage: (answererType: AnswererType, doctorId?: string | null) => void;
  onClickOuterLink: (url: string) => void;
  questionPid: string;
  extractTextFromChildren: (children: React.ReactNode) => string;
  isAiAnswerChecked?: boolean;
  advertisementComponent: React.ReactNode;
}

const CheckIcon = () => (
  <svg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M9.12533 10.5833L7.91699 9.39581C7.76421 9.24303 7.57324 9.16664 7.34408 9.16664C7.11491 9.16664 6.91699 9.24998 6.75033 9.41664C6.59755 9.56942 6.52116 9.76387 6.52116 9.99998C6.52116 10.2361 6.59755 10.4305 6.75033 10.5833L8.54199 12.375C8.70866 12.5416 8.9031 12.625 9.12533 12.625C9.34755 12.625 9.54199 12.5416 9.70866 12.375L13.2503 8.83331C13.417 8.66664 13.4969 8.4722 13.4899 8.24998C13.483 8.02775 13.4031 7.83331 13.2503 7.66664C13.0837 7.49998 12.8857 7.41317 12.6566 7.40623C12.4274 7.39928 12.2295 7.47914 12.0628 7.64581L9.12533 10.5833ZM6.79199 18.125L5.58366 16.0833L3.29199 15.5833C3.08366 15.5416 2.91699 15.434 2.79199 15.2604C2.66699 15.0868 2.61838 14.8958 2.64616 14.6875L2.87533 12.3333L1.31283 10.5416C1.17394 10.3889 1.10449 10.2083 1.10449 9.99998C1.10449 9.79164 1.17394 9.61109 1.31283 9.45831L2.87533 7.66664L2.64616 5.31248C2.61838 5.10414 2.66699 4.91317 2.79199 4.73956C2.91699 4.56595 3.08366 4.45831 3.29199 4.41664L5.58366 3.91664L6.79199 1.87498C6.9031 1.69442 7.05588 1.57289 7.25033 1.51039C7.44477 1.44789 7.63921 1.45831 7.83366 1.54164L10.0003 2.45831L12.167 1.54164C12.3614 1.45831 12.5559 1.44789 12.7503 1.51039C12.9448 1.57289 13.0975 1.69442 13.2087 1.87498L14.417 3.91664L16.7087 4.41664C16.917 4.45831 17.0837 4.56595 17.2087 4.73956C17.3337 4.91317 17.3823 5.10414 17.3545 5.31248L17.1253 7.66664L18.6878 9.45831C18.8267 9.61109 18.8962 9.79164 18.8962 9.99998C18.8962 10.2083 18.8267 10.3889 18.6878 10.5416L17.1253 12.3333L17.3545 14.6875C17.3823 14.8958 17.3337 15.0868 17.2087 15.2604C17.0837 15.434 16.917 15.5416 16.7087 15.5833L14.417 16.0833L13.2087 18.125C13.0975 18.3055 12.9448 18.4271 12.7503 18.4896C12.5559 18.5521 12.3614 18.5416 12.167 18.4583L10.0003 17.5416L7.83366 18.4583C7.63921 18.5416 7.44477 18.5521 7.25033 18.4896C7.05588 18.4271 6.9031 18.3055 6.79199 18.125Z'
      fill='url(#paint0_linear_5995_6529)'
    />
    <defs>
      <linearGradient
        id='paint0_linear_5995_6529'
        x1='10.0003'
        y1='1.47021'
        x2='10.0003'
        y2='18.5297'
        gradientUnits='userSpaceOnUse'
      >
        <stop stop-color='#FF8D00' />
        <stop offset='1' stop-color='#F76707' />
      </linearGradient>
    </defs>
  </svg>
);

const AnswerDetail = ({
  answer,
  isAiAnswerChecked,
  categoryName,
  onShowModal,
  onClickLikeAnswer,
  onClickMagazine,
  onClickWriteReview,
  moveToHospitalDetail,
  moveToDoctorDetailPage,
  onClickOuterLink,
  questionPid,
  advertisementComponent,
  extractTextFromChildren,
}: AnswerDetailProps) => {
  return (
    <>
      {/* 답변한 의사/간호사 */}
      <AnswerWrapper>
        <AnswerTop
          onClick={() => {
            moveToDoctorDetailPage(answer.answerer.answererType, answer.answerer.answererPublicId);
          }}
        >
          <AnswerWriterInfo>
            {answer.answerer.answererType === AnswererType.Nurse ? (
              <NurseProfile url={answer.answerer.profileImageUrl} size={48} />
            ) : (
              <LazyImage
                width={48}
                height={48}
                objectFit={'cover'}
                loader={<DefaultDoctorProfile />}
                src={answer.answerer.profileImageUrl}
              />
            )}
            <AnswerWriter>
              <AnswerWriterName>
                {getQndAnswererMedicalStaffType(answer.answerer.answererType, answer.answerer.name)}
              </AnswerWriterName>
              <AnswerWriterHospital>
                <AnswerWriterHospitalName lineNumber={1} isSelectable>
                  {answer.answerer.affiliatedInstitutionName}
                </AnswerWriterHospitalName>
                <Dot size={3} areaWidth={16} color={theme.color.gray300} />
                <span>{answer.createdDate}</span>
              </AnswerWriterHospital>
            </AnswerWriter>
            <Time dateTime={absoluteTime(answer.createdAt)} />
          </AnswerWriterInfo>
          <ReportButton
            type='button'
            onClick={(e) =>
              onShowModal(e, {
                report: {
                  targetId: answer.answerPid,
                  targetType: REPORT_TYPE.ANSWER,
                  roleId: answer.answerer.answererId,
                  role: answer.answerer.answererType === ROLE_TYPE.AI ? ROLE_TYPE.AI : ROLE_TYPE.DOCTOR,
                  isBlocked: answer.isBlockedByMe,
                },
              })
            }
          >
            <DotMenu />
          </ReportButton>
        </AnswerTop>

        {/* 답변 (차단/미차단) */}
        {answer.isBlockedByMe ? (
          <BlockQuestion />
        ) : (
          <AnswerContentWrapper>
            {/* AI 답변 검수 완료 배지 */}
            {answer.answerer.answererType === AnswererType.Ai && isAiAnswerChecked && (
              <VerifiedAnswerBadge>
                <CheckIcon />
                <span>의료진이 검수를 완료한 답변이에요</span>
              </VerifiedAnswerBadge>
            )}
            {answer.answerer.answererType !== AnswererType.Doctor ? (
              <MarkdownAnswerContent>
                {answer.answerer.answererType === AnswererType.Ai && (
                  <>
                    <p
                      className={'whitespace-pre-line'}
                    >{`안녕하세요. 실시간 고민 해결사입니다.\n[${categoryName}]에 관한 고민이 있으시군요. 질문에 대해 답변드리겠습니다.`}</p>
                  </>
                )}
                <ContentMarkDown
                  markdown={getAnswerDetail(answer)}
                  components={{
                    del: ({ children }) => <>~{children}~</>,
                    a: (node) => {
                      return (
                        <ClickLogger
                          params={{
                            eventName: FAQ_LOG_EVENT.CLICK_OUTER_LINK,
                            payload: {
                              questionPid: questionPid ?? '',
                              linkUrl: node.href ?? '',
                              linkText: extractTextFromChildren(node.children),
                            },
                          }}
                        >
                          <LinkTag
                            href={node.href ?? ''}
                            onClickNavigateToScreen={() => onClickOuterLink(node.href ?? '')}
                            target='_blank'
                            className='qna-link'
                            wrapperStyle={{
                              display: 'inline-block',
                            }}
                          >
                            {node.children}
                          </LinkTag>
                        </ClickLogger>
                      );
                    },
                  }}
                />
                {answer.answerer.answererType === AnswererType.Ai && (
                  <>
                    <p>닥터나우가 의료진과 함께 개발하고 파인튜닝한 건강 정보 AI의 답변입니다.</p>
                    <p>도움이 필요하실 때 언제든지 다시 이용해 주세요.</p>
                  </>
                )}
              </MarkdownAnswerContent>
            ) : (
              <TextAnswerContent>{getAnswerDetail(answer)}</TextAnswerContent>
            )}

            {answer.recommendedMagazineGraph && (
              <RecommendMagazine>
                <h3>선생님이 추천한 콘텐츠예요</h3>
                <RecommendMagazineWrapper
                  role='button'
                  onClick={() => onClickMagazine(answer.recommendedMagazineGraph?.magazinePid ?? '')}
                >
                  <RecommendMagazineContent>
                    <h4>{answer.recommendedMagazineGraph.title}</h4>
                    <p>{answer.recommendedMagazineGraph.description}</p>
                  </RecommendMagazineContent>
                  <RecommendMagazineImage src={answer.recommendedMagazineGraph.titleImageUrl} />
                </RecommendMagazineWrapper>
              </RecommendMagazine>
            )}

            {/* 답변 작성 의사 병원 정보 */}
            {answer.answerer.answererType === AnswererType.Doctor && !!answer.answerer.affiliation && (
              <AnswererHospital affiliation={answer.answerer.affiliation} moveToHospitalDetail={moveToHospitalDetail} />
            )}

            {advertisementComponent}

            {/* Footnote */}
            <AnswerFootnote answer={answer} />

            {/* NPS (만족해요/아쉬워요) */}
            {answer.isReviewViewable ? (
              answer.hasReview ? (
                <AnswerReviewListWrapper>
                  {answer.reviews.map((review) => (
                    <AnswerReview key={`answer-review-${review.id}`} review={review} />
                  ))}
                </AnswerReviewListWrapper>
              ) : (
                <AnswerWriteReview
                  title={'닥터나우 의료진 답변 어떠셨나요?'}
                  description={'목소리를 듣고 좋은 서비스를 제공해드릴게요.'}
                  onClickWriteReview={() => {
                    onClickWriteReview(answer.answerPid);
                  }}
                />
              )
            ) : (
              <AnswerNps>
                <NpsButton
                  isChecked={answer.myLikeType === AnswerLikeType.Like}
                  onClick={() => onClickLikeAnswer(answer.answerPid, AnswerLikeType.Like, answer.myLikeType)}
                >
                  <MaterialIcon
                    name={'thumb_up'}
                    size={16}
                    filled={answer.myLikeType === AnswerLikeType.Like}
                    color={answer.myLikeType === AnswerLikeType.Like ? theme.color.primary600 : theme.color.gray600}
                  />
                  만족해요
                  <LikeCount $display={!!answer.likeCount}>{answer.likeCount}</LikeCount>
                </NpsButton>

                <NpsButton
                  isChecked={answer.myLikeType === AnswerLikeType.Dislike}
                  onClick={() => onClickLikeAnswer(answer.answerPid, AnswerLikeType.Dislike, answer.myLikeType)}
                >
                  <MaterialIcon
                    name={'thumb_down'}
                    size={16}
                    filled={answer.myLikeType === AnswerLikeType.Dislike}
                    color={answer.myLikeType === AnswerLikeType.Dislike ? theme.color.primary600 : theme.color.gray600}
                  />
                  아쉬워요
                  <LikeCount $display={!!answer.dislikeCount}>{answer.dislikeCount}</LikeCount>
                </NpsButton>
              </AnswerNps>
            )}
          </AnswerContentWrapper>
        )}
      </AnswerWrapper>
      <Divider height={'15px'} color={theme.color.gray30} />
    </>
  );
};

export default AnswerDetail;

const AnswerWrapper = styled.li`
  background: ${({ theme }) => theme.color.white};

  cursor: default;
  &:hover {
    cursor: default;
  }
`;

const AnswerTop = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 20px 24px;
  align-items: center;

  ${({ theme }) => `
    border-bottom: 1px solid ${theme.color.gray30};
  `};
`;

const AnswerWriterInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: start;
`;

const AnswerWriter = styled.div`
  display: flex;
  flex-flow: column;
  margin-left: 16px;
  align-items: flex-start;
`;

const AnswerWriterName = styled.div`
  ${({ theme }) => `
    ${theme.typography.UI16SB};
    color: ${theme.color.gray700};
  `};
`;

const AnswerWriterHospital = styled.div`
  display: flex;
  flex: 1 1;
  align-items: center;

  ${({ theme }) => `
    color: ${theme.color.gray500};
    span {
      ${theme.typography.UI14M};
    }
  `};
`;

const AnswerWriterHospitalName = styled(LineClampTextStyled)`
  flex: 1;
  ${({ theme }) => `
    ${theme.typography.UI14M};
    `};
`;

const Time = styled.time`
  visibility: hidden;
`;

const ReportButton = styled(DefaultButtonStyled)`
  width: 32px;
  height: 32px;
  border-radius: 4px;

  &:active {
    background: ${({ theme }) => theme.color.gray50};
  }
`;

const AnswerContentWrapper = styled.div`
  padding: 24px;
  ${({ theme }) => `
    border-bottom: 1px solid ${theme.color.gray50};
  `};
`;

const TextAnswerContent = styled.div`
  white-space: pre-wrap;
  ${({ theme }) => `
    ${theme.typography.ARTICLE16R};
    color: ${theme.color.gray500};
    `}
`;

const MarkdownAnswerContent = styled.div`
  ${({ theme }) => `
    ${theme.typography.ARTICLE16R};
    color: ${theme.color.gray500};
    `}

  p {
    margin-bottom: 1.2em;
  }

  a, .qna-link {
    color: ${theme.color.blue600};
    font-weight: 500;
  }
    
  ol {
    list-style: decimal;
    margin-bottom: 1.2em;
    li {
      margin-left: 20px;
    }
  }

  ul {
    margin-bottom: 1.2em;
    list-style: circle;
    li {
      margin-left: 20px;
    }
  }
`;

const AnswerNps = styled.div`
  margin-top: 24px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: 8px;
`;

const NpsButton = styled(DefaultButtonStyled)<{ isChecked: boolean }>`
  padding: 10px 0;
  box-sizing: border-box;
  font-weight: 600;
  font-size: 14px;
  vertical-align: middle;
  border-radius: 5px;
  ${({ isChecked, theme }) => `
    ${theme.typography.UI14SB};
    color: ${isChecked ? theme.color.primary500 : theme.color.gray600};
    border: 1px solid
      ${isChecked ? theme.color.primary500 : theme.color.gray200};
      `};

  gap: 8px;
`;

const LikeCount = styled.span<{ $display: boolean }>`
  ${({ theme, $display }) => `
      display: ${$display ? 'block' : 'none'};
      ${theme.typography.UI14SB};
      `};
`;

const RecommendMagazine = styled.div`
  margin-top: 24px;
  width: 100%;
  display: flex;
  flex-direction: column;

  h3 {
    ${({ theme }) => theme.typography.UI16SB};
    color: ${({ theme }) => theme.color.gray700};
    margin-bottom: 12px;
  }
`;

const RecommendMagazineWrapper = styled.section`
  display: flex;
  border: 1px solid ${({ theme }) => theme.color.gray100};
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  width: 100%;
  max-height: 130px;
`;

const RecommendMagazineContent = styled.div`
  padding: 18px 16px;
  flex: 1;

  & > h4 {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 1;
    margin: 0 0 4px 0;
    width: 100%;
    ${({ theme }) => `
      ${theme.typography.UI14SB};
      color: ${theme.color.primary500};
      `};
  }

  & > p {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    ${({ theme }) => `
      ${theme.typography.UI12M}
      color: ${theme.color.gray500};
      `};
  }
`;

const RecommendMagazineImage = styled.img`
  width: 96px;
  height: 96px;
  object-fit: cover;
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
`;

const AnswerReviewListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const VerifiedAnswerBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 12px 16px;
  background-color: #FFF4E6;
  border-radius: 12px;
  margin-bottom: 20px;
  
  span {
    ${({ theme }) => theme.typography.UI15SB};
    color: #F76707;
  }
`;
